import React, { FC, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import type { BlinderOaxacaData } from "../../hooks";

import { Card, CardContent } from "@components/base";

interface PaygapBlinderOaxacaProps {
  data: BlinderOaxacaData[];
}

const GRADE_LABELS: Record<string, string> = {
  "grade-1": "Grade 1",
  "grade-2": "Grade 2",
  "grade-3": "Grade 3",
};

const COMPONENT_COLORS = {
  unadjustedGap: "#1976d2",
  explainedByTraits: "#388e3c",
  unexplainedByTraits: "#f57c00",
};

const BAR_WIDTH = 80;
const GROUP_SPACING = 60;
const LEFT_PADDING = 70;
const RIGHT_PADDING = 40;
const TOP_PADDING = 40;
const BOTTOM_PADDING = 60;
const CHART_HEIGHT = 420;

const PaygapBlinderOaxaca: FC<PaygapBlinderOaxacaProps> = ({ data }) => {
  const { t } = useTranslation();
  const [selectedGrade, setSelectedGrade] = useState<string>("all");

  const availableGrades = useMemo(
    () => [...new Set(data.map((d) => d.grade))].sort(),
    [data],
  );

  const filteredData = useMemo(() => {
    if (selectedGrade === "all") return data;

    return data.filter((d) => d.grade === selectedGrade);
  }, [data, selectedGrade]);

  const getGradeLabel = (grade: string): string =>
    GRADE_LABELS[grade] ?? grade;

  // Collect all bar values for scale calculation
  const maxAbsValue = useMemo(() => {
    if (filteredData.length === 0) return 10;

    let maxVal = 10;

    for (const d of filteredData) {
      maxVal = Math.max(
        maxVal,
        Math.abs(d.unadjustedGap),
        Math.abs(d.explainedByTraits),
        Math.abs(d.unexplainedByTraits),
      );
    }

    return maxVal;
  }, [filteredData]);

  const rawScale = Math.ceil(maxAbsValue / 10) * 10;
  const maxScale = Number.isFinite(rawScale) ? Math.min(rawScale, 500) : 100;

  // 3 bars per grade group
  const barsPerGroup = 3;
  const subBarWidth = BAR_WIDTH;
  const groupWidth = barsPerGroup * subBarWidth + (barsPerGroup - 1) * 4;

  const chartWidth = Math.max(
    600,
    LEFT_PADDING + filteredData.length * (groupWidth + GROUP_SPACING) + RIGHT_PADDING,
  );
  const contentHeight = CHART_HEIGHT - TOP_PADDING - BOTTOM_PADDING;
  const centerY = TOP_PADDING + contentHeight / 2;

  const valueToY = (value: number): number =>
    centerY - (value / maxScale) * (contentHeight / 2);

  const tickStep = maxScale <= 20 ? 5 : maxScale <= 100 ? 10 : Math.ceil(maxScale / 10 / 5) * 5;
  const ticks: number[] = [];

  for (let i = -maxScale; i <= maxScale && ticks.length < 200; i += tickStep) {
    ticks.push(i);
  }

  if (filteredData.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            {t("paygap.blinderOaxaca.title", "Blinder-Oaxaca Decomposition")}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={300}>
            <Typography variant="body2" color="text.secondary">
              {t("paygap.chart.noData", "No data available")}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6">
            {t("paygap.blinderOaxaca.title", "Blinder-Oaxaca Decomposition")}
          </Typography>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>
                {t("paygap.blinderOaxaca.gradingFilter", "Grading Specification")}
              </InputLabel>
              <Select
                value={selectedGrade}
                label={t("paygap.blinderOaxaca.gradingFilter", "Grading Specification")}
                onChange={(e) => setSelectedGrade(e.target.value)}
              >
                <MenuItem value="all">
                  {t("paygap.chart.allGradings", "All Gradings")}
                </MenuItem>
                {availableGrades.map((g) => (
                  <MenuItem key={g} value={g}>
                    {getGradeLabel(g)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={t("paygap.blinderOaxaca.tooltip", "The Blinder-Oaxaca decomposition splits the raw gender pay gap into an explained part (due to observable traits like education, experience, job grade) and an unexplained part (which may reflect discrimination or unmeasured factors).")}>
              <IconButton size="small">
                <Info />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" sx={{ overflowX: "auto" }}>
          <Box position="relative" width={chartWidth} height={CHART_HEIGHT} pb={2}>
            <svg width={chartWidth} height={CHART_HEIGHT}>
              {/* Y-axis label */}
              <text
                x={15}
                y={CHART_HEIGHT / 2}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
                transform={`rotate(-90, 15, ${CHART_HEIGHT / 2})`}
              >
                %
              </text>

              {/* Grid lines + tick labels */}
              {ticks.map((tick) => {
                const y = valueToY(tick);

                return (
                  <g key={tick}>
                    <line
                      x1={LEFT_PADDING}
                      y1={y}
                      x2={chartWidth - RIGHT_PADDING}
                      y2={y}
                      stroke={tick === 0 ? "#333" : "#e0e0e0"}
                      strokeWidth={tick === 0 ? 2 : 1}
                      strokeDasharray={tick === 0 ? undefined : "2,2"}
                    />
                    <text x={LEFT_PADDING - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#666">
                      {tick}%
                    </text>
                  </g>
                );
              })}

              {/* Data: 3 bars per grade (unadjusted, explained, unexplained) */}
              {filteredData.map((d, i) => {
                const groupX = LEFT_PADDING + i * (groupWidth + GROUP_SPACING) + GROUP_SPACING / 2;

                const bars: Array<{ value: number; color: string; key: string }> = [
                  { value: d.unadjustedGap, color: COMPONENT_COLORS.unadjustedGap, key: "unadjusted" },
                  { value: d.explainedByTraits, color: COMPONENT_COLORS.explainedByTraits, key: "explained" },
                  { value: d.unexplainedByTraits, color: COMPONENT_COLORS.unexplainedByTraits, key: "unexplained" },
                ];

                return (
                  <g key={d.grade}>
                    {bars.map((bar, barIdx) => {
                      const x = groupX + barIdx * (subBarWidth + 4);
                      const barTopY = bar.value >= 0 ? valueToY(bar.value) : centerY;
                      const barBottomY = bar.value >= 0 ? centerY : valueToY(bar.value);
                      const barHeight = Math.abs(barBottomY - barTopY);

                      return (
                        <g key={bar.key}>
                          {barHeight > 0 && (
                            <rect
                              x={x}
                              y={barTopY}
                              width={subBarWidth}
                              height={barHeight}
                              fill={bar.color}
                              opacity={0.8}
                              rx={2}
                            />
                          )}
                          <text
                            x={x + subBarWidth / 2}
                            y={bar.value >= 0 ? barTopY - 5 : barBottomY + 14}
                            textAnchor="middle"
                            fontSize="9"
                            fill="#333"
                            fontWeight="500"
                          >
                            {bar.value.toFixed(1)}%
                          </text>
                        </g>
                      );
                    })}

                    {/* Grade label centered under the group */}
                    <text
                      x={groupX + groupWidth / 2}
                      y={CHART_HEIGHT - BOTTOM_PADDING + 20}
                      textAnchor="middle"
                      fontSize="11"
                      fill="#333"
                      fontWeight="bold"
                    >
                      {getGradeLabel(d.grade)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Box>
        </Box>

        {/* Legend */}
        <Box display="flex" gap={3} mt={2} justifyContent="center" flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 14,
                height: 14,
                bgcolor: COMPONENT_COLORS.unadjustedGap,
                borderRadius: 0.5,
                opacity: 0.8,
              }}
            />
            <Typography variant="caption">
              {t("paygap.blinderOaxaca.unadjustedGap", "Unadjusted Gap")}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 14,
                height: 14,
                bgcolor: COMPONENT_COLORS.explainedByTraits,
                borderRadius: 0.5,
                opacity: 0.8,
              }}
            />
            <Typography variant="caption">
              {t("paygap.blinderOaxaca.explained", "Explained by Traits")}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 14,
                height: 14,
                bgcolor: COMPONENT_COLORS.unexplainedByTraits,
                borderRadius: 0.5,
                opacity: 0.8,
              }}
            />
            <Typography variant="caption">
              {t("paygap.blinderOaxaca.unexplained", "Unexplained by Traits")}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaygapBlinderOaxaca;
